/**
 * Bikefanatics: shared "recoloured bike" hero stage.
 *
 * window.BfHeroStage(root, palette, captionEl)
 *   root     - element containing [data-bf-hub-cards] and a <canvas data-bf-hub-bike>
 *   palette  - array of hex colours for the rotating 3D cards
 *   setBike(src, refHex, label) - model PNG (transparent bg, CORS "*"),
 *              reference frame colour (attributes.extractedFrameColorFromImage), caption
 *
 * A CSS 3D carousel of paint-colour cards rotates behind the bike; pixels of
 * the bike image whose hue is close to the reference frame colour are shifted
 * to the hue/saturation of the card in front while keeping their lightness,
 * so the frame recolours but shading, decals and components stay intact.
 * Used by sections/bf-configurator-hub.liquid and sections/bf-configurator-banner.liquid.
 */
(function () {
  'use strict';

  function el(tag, cls) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    return n;
  }

  function hexToRgb(hex) {
    var h = (hex || '').replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var n = parseInt(h, 16);
    if (isNaN(n) || h.length !== 6) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b), h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return [h, s, l];
  }

  function hslToRgb(h, s, l) {
    if (s === 0) { var v = Math.round(l * 255); return [v, v, v]; }
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    return [Math.round(hue2rgb(p, q, h + 1 / 3) * 255), Math.round(hue2rgb(p, q, h) * 255), Math.round(hue2rgb(p, q, h - 1 / 3) * 255)];
  }

  function hueDist(a, b) {
    var d = Math.abs(a - b);
    return Math.min(d, 1 - d);
  }

  function HeroStage(root, palette, caption, options) {
    this.root = root;
    this.cardsWrap = root.querySelector('[data-bf-hub-cards]');
    this.canvas = root.querySelector('[data-bf-hub-bike]');
    this.caption = caption || null;
    this.palette = palette.filter(hexToRgb);
    this.interval = (options && options.interval) || 3200;
    this.index = 0;
    this.cards = [];
    this.timer = null;
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.buildCards();
  }

  HeroStage.prototype.buildCards = function () {
    if (!this.cardsWrap) return;
    this.cardsWrap.innerHTML = '';
    this.cards = [];
    for (var i = 0; i < this.palette.length; i++) {
      var c = el('span', 'bf-hub__card-color');
      c.style.setProperty('--card-color', this.palette[i]);
      this.cardsWrap.appendChild(c);
      this.cards.push(c);
    }
    this.layout();
  };

  HeroStage.prototype.layout = function () {
    var n = this.cards.length;
    for (var i = 0; i < n; i++) {
      var rel = ((i - this.index) % n + n) % n;
      if (rel > n / 2) rel -= n;
      var abs = Math.abs(rel);
      var card = this.cards[i];
      card.style.transform = 'translate3d(' + (rel * 48) + '%, ' + (abs * 4) + '%, ' + (-abs * 140) + 'px) rotateY(' + (rel * -34) + 'deg)';
      card.style.opacity = abs > 2 ? 0 : (1 - abs * 0.22).toFixed(2);
      card.style.filter = abs ? 'brightness(' + (1 - abs * 0.18).toFixed(2) + ')' : 'none';
      card.style.zIndex = String(10 - abs);
    }
  };

  HeroStage.prototype.setBike = function (src, refHex, label) {
    var self = this;
    this.refHex = refHex || '#EAC909';
    this.label = label || '';
    if (this.caption) this.caption.textContent = this.label;
    if (!this.canvas) { this.start(); return; }
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self.img = img;
      self.canvas.width = img.naturalWidth;
      self.canvas.height = img.naturalHeight;
      self.prepare();
      self.paint(self.palette[self.index]);
      self.start();
    };
    img.onerror = function () {
      var fallback = new Image();
      fallback.className = self.canvas.className + ' bf-hub__bike--static';
      fallback.src = src;
      fallback.alt = label || '';
      self.canvas.replaceWith(fallback);
      self.canvas = null;
      self.start();
    };
    img.src = src;
  };

  HeroStage.prototype.prepare = function () {
    var ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.img, 0, 0);
    var data;
    try {
      data = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    } catch (e) {
      this.base = null;
      return;
    }
    this.base = data;
    var ref = hexToRgb(this.refHex) || [234, 201, 9];
    var refHsl = rgbToHsl(ref[0], ref[1], ref[2]);
    var refIsGrey = refHsl[1] < 0.2;
    var px = data.data, n = px.length / 4;
    var weight = new Float32Array(n);
    var light = new Float32Array(n);
    for (var i = 0; i < n; i++) {
      if (px[i * 4 + 3] < 10) continue;
      var h = rgbToHsl(px[i * 4], px[i * 4 + 1], px[i * 4 + 2]);
      light[i] = h[2];
      var w;
      if (refIsGrey) {
        w = (h[1] < 0.25 && h[2] > 0.08 && h[2] < 0.6) ? 1 : 0;
      } else {
        var dh = hueDist(h[0], refHsl[0]);
        if (h[1] < 0.22 || h[2] < 0.06 || h[2] > 0.96) w = 0;
        else w = Math.max(0, 1 - dh / 0.09);
      }
      weight[i] = w;
    }
    this.weight = weight;
    this.light = light;
  };

  HeroStage.prototype.paint = function (hex) {
    if (!this.canvas) return;
    if (this.layers) { this.paintLayered(hex); return; }
    if (!this.img) return;
    var ctx = this.canvas.getContext('2d');
    if (!this.base || !this.weight) {
      ctx.drawImage(this.img, 0, 0);
      return;
    }
    var target = hexToRgb(hex);
    if (!target) return;
    var tHsl = rgbToHsl(target[0], target[1], target[2]);
    var out = ctx.createImageData(this.base.width, this.base.height);
    var src = this.base.data, dst = out.data, w = this.weight, light = this.light, n = w.length;
    for (var i = 0; i < n; i++) {
      var o = i * 4;
      dst[o + 3] = src[o + 3];
      if (w[i] <= 0) {
        dst[o] = src[o]; dst[o + 1] = src[o + 1]; dst[o + 2] = src[o + 2];
        continue;
      }
      var l2 = light[i] * 0.7 + tHsl[2] * 0.3;
      var rgb = hslToRgb(tHsl[0], Math.min(1, tHsl[1] * 1.05), l2);
      var k = w[i];
      dst[o] = Math.round(src[o] * (1 - k) + rgb[0] * k);
      dst[o + 1] = Math.round(src[o + 1] * (1 - k) + rgb[1] * k);
      dst[o + 2] = Math.round(src[o + 2] * (1 - k) + rgb[2] * k);
    }
    ctx.putImageData(out, 0, 0);
  };

  /* ---------- layered (BCF paint masks) mode ---------- */

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('load ' + src)); };
      img.src = src;
    });
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  /** BCF masks are opaque black/white PNGs: red channel = coverage. Convert to an alpha mask. */
  function maskToAlpha(img, w, h) {
    var c = makeCanvas(w, h), ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h), px = d.data;
    for (var i = 0; i < px.length; i += 4) {
      px[i + 3] = Math.round(px[i] * (px[i + 3] / 255));
      px[i] = px[i + 1] = px[i + 2] = 0;
    }
    ctx.putImageData(d, 0, 0);
    return c;
  }

  function maskCoverage(c) {
    var d = c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data, n = 0;
    for (var i = 3; i < d.length; i += 4) if (d[i] > 0) n++;
    return n;
  }

  function luminance(hex) {
    var rgb = hexToRgb(hex) || [0, 0, 0];
    return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  }

  function shade(hex, factor) {
    var rgb = hexToRgb(hex) || [0, 0, 0];
    var hsl = rgbToHsl(rgb[0], rgb[1], rgb[2]);
    var out = hslToRgb(hsl[0], hsl[1], Math.max(0, Math.min(1, hsl[2] * factor)));
    return 'rgb(' + out[0] + ',' + out[1] + ',' + out[2] + ')';
  }

  /**
   * entry = one model from assets/bf-hero-layers.json:
   *   { paint: {base, 'highlight-gloss', …}, masks: {'mask-1','mask-2','mask-3','mask-fixed', fixed, 'glossy-map'}, components: [{src, order}] }
   * Renders like the BCF compositor: components with order <= 7 under the paint,
   * paint zones = base (white shaded frame) × zone colour, masked; decals (fixed)
   * tinted black/white by contrast; gloss highlights added; components > 7 on top.
   */
  /** Load all images of one manifest entry and prepare masks/highlights. Resolves to a layers object. */
  HeroStage.prototype.loadEntry = function (entry) {
    var jobs = { base: loadImage(entry.paint.base) };
    if (entry.paint['highlight-gloss']) jobs.gloss = loadImage(entry.paint['highlight-gloss']);
    if (entry.paint['highlight-matte']) jobs.matte = loadImage(entry.paint['highlight-matte']);
    ['mask-1', 'mask-2', 'mask-3', 'mask-fixed', 'fixed', 'glossy-map'].forEach(function (k) {
      if (entry.masks && entry.masks[k]) jobs[k] = loadImage(entry.masks[k]);
    });
    var comps = (entry.components || []).slice().sort(function (a, b) { return a.order - b.order; });
    var compJobs = comps.map(function (c) { return loadImage(c.src).catch(function () { return null; }); });
    var keys = Object.keys(jobs);
    return Promise.all(keys.map(function (k) { return jobs[k]; })).then(function (imgs) {
      var L = {};
      keys.forEach(function (k, i) { L[k] = imgs[i]; });
      return Promise.all(compJobs).then(function (compImgs) {
        var w = L.base.naturalWidth, h = L.base.naturalHeight;
        var layers = {
          w: w, h: h, base: L.base, fixed: L.fixed || null,
          highlight: buildHighlight(L.gloss, L.matte, L['glossy-map'], w, h),
          masks: [L['mask-1'], L['mask-2'], L['mask-3']].filter(Boolean).map(function (m) { return maskToAlpha(m, w, h); }),
          maskFixed: L['mask-fixed'] ? maskToAlpha(L['mask-fixed'], w, h) : null,
          under: [], over: []
        };
        layers.coverage = layers.masks.map(maskCoverage);
        compImgs.forEach(function (img, i) {
          if (!img) return;
          (comps[i].order <= 7 ? layers.under : layers.over).push(img);
        });
        return layers;
      });
    });
  };

  HeroStage.prototype.useLayers = function (layers) {
    this.layers = layers;
    this.canvas.width = layers.w;
    this.canvas.height = layers.h;
    this.base = null; // disable hue-recolour path
    if (!this.scratch || this.scratch.width !== layers.w) {
      this.scratch = makeCanvas(layers.w, layers.h);
      this.scratch2 = makeCanvas(layers.w, layers.h);
    }
  };

  /** One model, recoloured through the palette (hub/banner "layered" mode). */
  HeroStage.prototype.setLayered = function (entry, label) {
    var self = this;
    this.label = label || '';
    if (this.caption) this.caption.textContent = this.label;
    if (!this.canvas || !entry || !entry.paint || !entry.paint.base) { this.start(); return Promise.resolve(false); }
    return this.loadEntry(entry).then(function (layers) {
      self.useLayers(layers);
      self.paint(self.palette[self.index]);
      self.start();
      return true;
    }).catch(function () {
      self.start();
      return false;
    });
  };

  /**
   * Cards mode (like ridley-bikes.com): every colour card is its own build –
   * model, colour, design and components. cards = manifest.cards. The first card
   * loads immediately, the rest in the background; the carousel only advances to
   * cards that are ready.
   */
  HeroStage.prototype.setCards = function (cards) {
    var self = this;
    cards = (cards || []).filter(function (c) { return c && c.hex && c.paint && c.paint.base && hexToRgb(c.hex); });
    if (!this.canvas || !cards.length) { this.start(); return Promise.resolve(false); }
    this.builds = cards;
    this.cardLayers = new Array(cards.length);
    this.palette = cards.map(function (c) { return c.hex; });
    this.index = 0;
    this.buildCards();

    var loadAt = function (i) {
      return self.loadEntry(cards[i]).then(function (layers) { self.cardLayers[i] = layers; return layers; })
        .catch(function () { self.cardLayers[i] = null; return null; });
    };
    return loadAt(0).then(function (layers) {
      if (!layers) { self.start(); return false; }
      self.showCard(0);
      self.start();
      // background-load the others one after another
      var i = 1;
      var nextLoad = function () { if (i < cards.length) loadAt(i++).then(nextLoad); };
      nextLoad();
      return true;
    });
  };

  HeroStage.prototype.showCard = function (i) {
    var card = this.builds[i], layers = this.cardLayers[i];
    if (!card || !layers) return false;
    this.useLayers(layers);
    this.label = card.label || '';
    if (this.caption) this.caption.textContent = this.label;
    this.paintLayered(card.hex);
    return true;
  };

  /**
   * BCF shader: highlights = (gloss.rgb*gloss.a*gf + matte.rgb*matte.a*(1-gf)) * glossyMap.a,
   * gf = (1-map.r)*glossiness + map.r*map.g. We render a glossy finish (glossiness 1).
   * Returns a premultiplied canvas (alpha = highlight intensity) to add with 'lighter'.
   */
  function buildHighlight(gloss, matte, map, w, h) {
    if (!gloss && !matte) return null;
    var read = function (img) {
      if (!img) return null;
      var c = makeCanvas(w, h), ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      return ctx.getImageData(0, 0, w, h).data;
    };
    var g = read(gloss), m = read(matte), gm = read(map);
    var out = makeCanvas(w, h), octx = out.getContext('2d');
    var od = octx.createImageData(w, h), px = od.data;
    for (var i = 0; i < px.length; i += 4) {
      var mapA = gm ? gm[i + 3] / 255 : 1;
      var mapR = gm ? gm[i] / 255 : 0, mapG = gm ? gm[i + 1] / 255 : 0;
      var gf = (1 - mapR) * 1 + mapR * mapG;
      var r = 0, gch = 0, b = 0;
      if (g) { var ga = g[i + 3] / 255 * gf; r += g[i] * ga; gch += g[i + 1] * ga; b += g[i + 2] * ga; }
      if (m) { var ma = m[i + 3] / 255 * (1 - gf); r += m[i] * ma; gch += m[i + 1] * ma; b += m[i + 2] * ma; }
      r *= mapA; gch *= mapA; b *= mapA;
      var inten = Math.min(255, Math.max(r, gch, b));
      if (inten < 2) continue;
      // store straight colour with alpha = intensity so 'lighter' adds rgb*alpha ≈ highlight
      px[i] = Math.min(255, Math.round(r / inten * 255));
      px[i + 1] = Math.min(255, Math.round(gch / inten * 255));
      px[i + 2] = Math.min(255, Math.round(b / inten * 255));
      px[i + 3] = Math.round(inten);
    }
    octx.putImageData(od, 0, 0);
    return out;
  }

  /** One BCF "masked-layer": image × colour, + highlights, clipped by mask. */
  HeroStage.prototype.paintLayer = function (ctxOut, image, color, maskCanvas, withHighlights) {
    var Ls = this.layers, w = Ls.w, h = Ls.h;
    var t = this.scratch.getContext('2d');
    t.globalCompositeOperation = 'source-over';
    t.globalAlpha = 1;
    t.clearRect(0, 0, w, h);
    t.fillStyle = color;
    t.fillRect(0, 0, w, h);
    t.globalCompositeOperation = 'multiply';
    t.drawImage(image, 0, 0, w, h);
    t.globalCompositeOperation = 'destination-in';
    t.drawImage(image, 0, 0, w, h);
    if (maskCanvas) t.drawImage(maskCanvas, 0, 0, w, h);
    if (withHighlights && Ls.highlight) {
      t.globalCompositeOperation = 'lighter';
      t.drawImage(Ls.highlight, 0, 0, w, h);
      t.globalCompositeOperation = 'destination-in';
      t.drawImage(image, 0, 0, w, h);
      if (maskCanvas) t.drawImage(maskCanvas, 0, 0, w, h);
    }
    t.globalCompositeOperation = 'source-over';
    ctxOut.drawImage(this.scratch, 0, 0, w, h);
  };

  HeroStage.prototype.paintLayered = function (hex) {
    var Ls = this.layers, w = Ls.w, h = Ls.h;
    var ctx = this.canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    Ls.under.forEach(function (img) { ctx.drawImage(img, 0, 0, w, h); });

    // Same zone logic as the BCF customizer: zone 1 = light accent (white, or light
    // grey when the logo is black), zone 2 = colour-1 (the card), zone 3 = logo colour.
    // Zone roles by coverage (the main zone differs per model: Ridley designs
    // use mask-2 for the main colour, Nukeproof mask-1): largest = card colour,
    // next = light accent, smallest = logo colour.
    // Hero = single-colour bike: every paint zone gets the card colour, only the
    // smallest zone (logo/lettering) gets the contrasting logo colour.
    var logo = luminance(hex) > 0.35 ? '#000000' : '#ffffff';
    var order = Ls.masks.map(function (_, i) { return i; }).sort(function (a, b) { return Ls.coverage[b] - Ls.coverage[a]; });
    for (var k = 0; k < order.length; k++) {
      var isLogo = order.length > 1 && k === order.length - 1 && Ls.coverage[order[k]] < Ls.coverage[order[0]] * 0.25;
      this.paintLayer(ctx, Ls.base, isLogo ? logo : hex, Ls.masks[order[k]], true);
    }

    if (Ls.fixed && Ls.maskFixed) this.paintLayer(ctx, Ls.fixed, logo, Ls.maskFixed, true);

    Ls.over.forEach(function (img) { ctx.drawImage(img, 0, 0, w, h); });
  };

  HeroStage.prototype.start = function () {
    var self = this;
    if (this.timer || this.reduced || this.palette.length < 2) return;
    this.timer = setInterval(function () { self.next(); }, this.interval);
    if (!this._visibilityBound) {
      this._visibilityBound = true;
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { clearInterval(self.timer); self.timer = null; }
        else if (!self.timer) self.start();
      });
    }
  };

  HeroStage.prototype.next = function () {
    if (this.builds) {
      // advance to the next build whose layers are loaded
      for (var step = 1; step <= this.builds.length; step++) {
        var i = (this.index + step) % this.builds.length;
        if (this.cardLayers[i]) {
          this.index = i;
          this.layout();
          this.showCard(i);
          return;
        }
      }
      return;
    }
    this.index = (this.index + 1) % this.palette.length;
    this.layout();
    this.paint(this.palette[this.index]);
  };

  HeroStage.hexToRgb = hexToRgb;
  HeroStage.loadImage = loadImage;
  HeroStage.rgbToHsl = rgbToHsl;
  window.BfHeroStage = HeroStage;
})();
